import { Component, effect, inject, OnInit } from '@angular/core';
import { HeaderComponent } from '../../atoms/header/header.component';
import { DataLayerTypeStore } from '../../../stores/data-layer-type/data-layer-type.state';
import { DataLayerType } from '../../../models/data-layer-type';
import { PoiHelperService } from '../../../services/data-layer-type/poi-helper.service';
import { CheckboxModule } from 'primeng/checkbox';
import { SvgIconComponent } from '../../atoms/svg-icon/svg-icon.component';
import { FormsModule } from '@angular/forms';
import { WidgetStore } from '../../../stores/grid/widget.state';
import {
  CustomGridStackWidget,
  WidgetConfig,
} from '../../../services/grid-widget-config.service';
import { SearchInputComponent } from '../../atoms/search-input/search-input.component';
import { PoiListComponent } from '../../molecules/poi-list/poi-list.component';
import { PoiTypeItemComponent } from '../../molecules/poi-type-item/poi-type-item.component';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { patchState } from '@ngrx/signals';
import { DividerModule } from 'primeng/divider';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { DropdownItem } from '../../../models/form/basic';
import { ColorsetDropdownComponent } from '../../atoms/colorset-dropdown/colorset-dropdown.component';
import { FormFieldWrapperComponent } from '../../molecules/form-field-wrapper/form-field-wrapper.component';
import { ChipsModule } from 'primeng/chips';
import { ColorsetStore } from '../../../stores/colorset/colorset.state';
import { AccordionModule } from 'primeng/accordion';
import { ActiveUserStore } from '../../../stores/user/active-user.state';

/**
 * Default isochrone minutes, used whenever the widget has no custom value
 * configured yet. Must be a stable reference (not a fresh array literal) -
 * `isochroneMinutes` is bound via `[(ngModel)]` on a `p-chips`, and returning
 * a new array on every read makes it look "changed" on every change-detection
 * cycle, which spins Angular/PrimeNG into a synchronous infinite loop that
 * locks up the tab entirely.
 */
const DEFAULT_ISOCHRONE_MINUTES = [15, 30, 45];

/**
 * Mapbox's Isochrone API rejects requests with more than 4 contours, so the
 * UI must never let a user configure more than that.
 */
const MAX_ISOCHRONE_INTERVALS = 4;

@Component({
  selector: 'app-poi-checkboxes',
  standalone: true,
  imports: [
    HeaderComponent,
    CheckboxModule,
    SvgIconComponent,
    FormsModule,
    SearchInputComponent,
    PoiListComponent,
    DividerModule,
    ButtonModule,
    DropdownModule,
    ColorsetDropdownComponent,
    FormFieldWrapperComponent,
    ChipsModule,
    AccordionModule,
    PoiTypeItemComponent,
  ],
  templateUrl: './poi-checkboxes.component.html',
  styleUrl: './poi-checkboxes.component.scss',
})
export class PoiCheckboxesComponent implements OnInit {
  readonly widgetStore = inject(WidgetStore);
  readonly layerStore = inject(DataLayerStore);
  readonly colorsetStore = inject(ColorsetStore);
  readonly activeUserStore = inject(ActiveUserStore);

  constructor(private poiHelperService: PoiHelperService) {
    effect(() => {
      const selected = this.widgetStore.selected();
      if (selected) {
        this.activeWidget = selected;
        this.config = selected.widget || null;
        this.setTypeSelectItems();
        this.initFromConfig();
      }
    });
  }

  activeWidget: CustomGridStackWidget | null = null;
  typeSelectItems: DataLayerType[] = [];
  selectedPoiIds: string[] = [];
  config: WidgetConfig | null = null;
  searchWord = '';
  openPoiKey = '';
  expandedCategories = new Set<string>();

  isochroneProfileOptions: DropdownItem[] = [
    { label: 'Auto', value: 'driving', icon: 'fas fa-car' },
    { label: 'Fahrrad', value: 'cycling', icon: 'fas fa-bicycle' },
    { label: 'Zu Fuß', value: 'walking', icon: 'fas fa-walking' },
  ];

  get isochroneMinutes(): number[] {
    return this.config?.map?.isochroneMinutes || DEFAULT_ISOCHRONE_MINUTES;
  }

  set isochroneMinutes(value: any[]) {
    if (!this.activeWidget?.widget?.map) return;
    // Ensure numbers and max 60
    const sanitized = value
      .map((v) => (typeof v === 'string' ? parseInt(v, 10) : v))
      .filter((v) => !isNaN(v))
      .map((v) => Math.min(v, 60))
      .slice(0, MAX_ISOCHRONE_INTERVALS);

    // Never allow the last remaining interval to be removed - an empty list
    // sends a request with no contours, which fails silently and leaves the
    // previous isochrone layer stuck on the map.
    if (sanitized.length === 0) return;

    this.activeWidget.widget.map.isochroneMinutes = sanitized;
    this.updateConfig();
  }

  get isochroneProfile(): string {
    return this.config?.map?.isochroneProfile || 'walking';
  }

  set isochroneProfile(value: 'driving' | 'walking' | 'cycling') {
    if (!this.activeWidget?.widget?.map) return;
    this.activeWidget.widget.map.isochroneProfile = value;
    this.updateConfig();
  }

  get isochroneColorset(): string | undefined {
    return this.config?.map?.isochroneColorset;
  }

  set isochroneColorset(value: string | undefined) {
    if (!this.activeWidget?.widget?.map) return;
    this.activeWidget.widget.map.isochroneColorset = value;
    this.updateConfig();
  }

  getIsochroneProfileLabel(): string {
    const profile = this.isochroneProfile;
    const option = this.isochroneProfileOptions.find((o) => o.value === profile);
    return option ? option.label : profile;
  }

  getIsochroneMinutesSummary(): string {
    const minutes = this.isochroneMinutes;
    if (!minutes || minutes.length === 0) return '-';
    return minutes.join(', ') + ' Min.';
  }

  getIsochroneColor(index: number): string {
    const colorsetId = this.isochroneColorset;
    if (!colorsetId) return '#5a3fc0';
    const colorset = this.colorsetStore.getById(colorsetId);
    if (!colorset || !colorset.colors.length) return '#5a3fc0';
    return colorset.colors[index % colorset.colors.length];
  }

  private updateConfig(): void {
    if (!this.activeWidget) return;
    this.widgetStore.updateWidgetConfigById(
      this.activeWidget.id,
      this.activeWidget.widget
    );
  }

  ngOnInit(): void {
    // Initial setup is handled by the effect, but we can keep it for safety or call it here
    const selected = this.widgetStore.selected();
    if (selected) {
      this.activeWidget = selected;
      this.config = selected.widget || null;
      this.setTypeSelectItems();
      this.initFromConfig();
    }
  }

  /**
   * Updates the typeSelectItems property with the available POI types
   * based on the active year and disabled types.
   *
   * @return {void} Does not return a value.
   */
  setTypeSelectItems(): void {
    this.typeSelectItems = this.poiHelperService.getAvailablePoiTypes(
      this.activeWidget?.widget.year
    );
  }

  /**
   * Initializes the component using the configuration provided.
   * Extracts points of interest (POIs) from the configuration and
   * updates the selectedPoiIds accordingly.
   *
   * @return {void} No return value.
   */
  private initFromConfig(): void {
    if (this.config?.map?.poi) {
      this.selectedPoiIds = this.config.map.poi.map(p => p.type);
    } else {
      this.selectedPoiIds = [];
    }
  }

  /**
   * Handles the event triggered when the selection changes.
   * Builds a POI configuration based on the selected items and emits it through the selectionChange event.
   *
   * @return {void} Does not return a value.
   */
  onSelectionChange(): void {
    if (!this.activeWidget?.widget?.map) return;
    const poiConfig = this.poiHelperService.buildPoiConfig(
      this.typeSelectItems.filter(t => this.selectedPoiIds.includes(t._id))
    );

    this.activeWidget.widget.map.poi = poiConfig.length ? poiConfig : undefined;

    this.widgetStore.updateWidgetConfigById(
      this.activeWidget.id,
      this.activeWidget.widget
    );
  }

  search(searchWord: string): void {
    this.searchWord = searchWord;
  }

  toggleCategory(id: string): void {
    if (this.expandedCategories.has(id)) {
      this.expandedCategories.delete(id);
    } else {
      this.expandedCategories.add(id);
    }
  }

  isExpanded(id: string): boolean {
    return this.expandedCategories.has(id) || !!this.searchWord;
  }

  callPoiClickEvent(key: string, typeId: string): void {
    const isClosing = this.openPoiKey === key;
    this.openPoiKey = isClosing ? '' : key;

    if (!isClosing && !this.selectedPoiIds.includes(typeId)) {
      this.selectedPoiIds = [...this.selectedPoiIds, typeId];
      this.onSelectionChange();
      setTimeout(() => this.widgetStore.triggerEvent('showPoi', { key }), 300);
    } else {
      queueMicrotask(() => this.widgetStore.triggerEvent('showPoi', { key }));
    }
  }
}
